import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreIcon, CheckCircleIcon, XCircleIcon, ClockIcon, SearchIcon, MapPinIcon, ExternalLinkIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import BackButton from '../../../components/UI/BackButton';
import { bookstores } from '../../../data/bookstoresData';
const BookstorePartnerships = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeTab, setActiveTab] = useState<'active' | 'requests' | 'discover'>('requests');
  // Mock data
  const activePartnerships = bookstores.slice(0, 2);
  const incomingRequests = bookstores.slice(2, 4);
  const suggestedStores = bookstores.slice(4, 7);
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'brick-and-mortar':
        return dk.isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-800';
      case 'online-only':
        return dk.isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
      default:
        return dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800';
    }
  };
  const formatType = (type: string) => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-18637" data-mp="mrh1eh65l14d">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18638" data-mp="hrcfgbmh7e6r">
        <div className="mb-6" data-id="element-18639" data-mp="czkwam5ngaqn">
          <BackButton data-id="element-18640" data-mp="cyjapnr0c5uo" />
        </div>

        {/* Header */}
        <div className="mb-8" data-id="element-18641" data-mp="80htz9h20imd">
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-18642" data-mp="819osi2tucbl">
            Bookstore Partnerships
          </h1>
          <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-18643" data-mp="15gnm29qic2st">
            Manage requests from bookstores wanting to carry your titles, and
            discover new retail partners.
          </p>
        </div>

        {/* Tabs */}
        <div className={`border-b mb-8 ${dk.border}`} data-id="element-18644" data-mp="2uz3zlq9nptga">
          <nav className="-mb-px flex space-x-8" data-id="element-18645" data-mp="2yyumhiw2bpfj">
            <button onClick={() => setActiveTab('requests')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'requests' ? `${rt.borderActive} ${rt.textPrimary}` : `border-transparent ${dk.textMuted} hover:${dk.borderAccent} hover:${dk.textPrimary}`}`} data-id="element-18646" data-mp="2z0paj62mrxnu">
              Requests
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === 'requests' ? rt.badgeBg + ' text-white' : dk.badgeNeutral}`} data-id="element-18647" data-mp="2g9o98be1fq14">
                2
              </span>
            </button>
            <button onClick={() => setActiveTab('active')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'active' ? `${rt.borderActive} ${rt.textPrimary}` : `border-transparent ${dk.textMuted} hover:${dk.borderAccent} hover:${dk.textPrimary}`}`} data-id="element-18648" data-mp="gz2eetqvh26">
              Active Partners
            </button>
            <button onClick={() => setActiveTab('discover')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'discover' ? `${rt.borderActive} ${rt.textPrimary}` : `border-transparent ${dk.textMuted} hover:${dk.borderAccent} hover:${dk.textPrimary}`}`} data-id="element-18649" data-mp="ocahrtvu22ww">
              Discover Stores
            </button>
          </nav>
        </div>

        {/* Tab Content: Requests */}
        {activeTab === 'requests' && <div className="space-y-8" data-id="element-18650" data-mp="139uksv965o0f">
            {/* Incoming */}
            <div data-id="element-18651" data-mp="137gpqmehobsv">
              <h2 className={`text-lg font-bold mb-4 ${dk.textPrimary}`} data-id="element-18652" data-mp="392jg3s1u4l5q">
                Incoming Requests
              </h2>
              <div className="space-y-4" data-id="element-18653" data-mp="351prewgkj2bs">
                {incomingRequests.map(store => <div key={store.id} className={`p-5 rounded-xl border shadow-sm flex flex-col sm:flex-row gap-5 items-start sm:items-center justify-between ${dk.card}`} data-id="element-18654" data-mp="36imuqmvxrzku">
                    <div className="flex items-center gap-4" data-id="element-18655" data-mp="3fjb6tc1rrpv3">
                      <img src={store.logo} alt={store.name} className="w-14 h-14 rounded-lg object-cover border border-gray-200 dark:border-gray-700" data-id="element-18656" data-mp="3kieb2u9xds20" />
                      <div data-id="element-18657" data-mp="2rtgvx010gbdh">
                        <div className="flex items-center gap-2 mb-1" data-id="element-18658" data-mp="2wsa9esgas2dn">
                          <h3 className={`font-bold text-lg ${dk.textPrimary}`} data-id="element-18659" data-mp="2wqz76x2i32on">
                            {store.name}
                          </h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getTypeColor(store.type)}`} data-id="element-18660" data-mp="244eftwm0yql3">
                            {formatType(store.type)}
                          </span>
                        </div>
                        <p className={`text-sm ${dk.textSecondary} mb-1`} data-id="element-18661" data-mp="1wjlqyezoskvb">
                          Wants to carry:{' '}
                          <span className="font-medium" data-id="element-18662" data-mp="1rjz89q13acf4">The Silent Echo</span>{' '}
                          (Signed Copies)
                        </p>
                        <div className={`flex items-center text-xs ${dk.textMuted}`} data-id="element-18663" data-mp="1ikc817k3jcnn">
                          <ClockIcon className="h-3 w-3 mr-1" data-id="element-18664" data-mp="1dsksrt6e4tct" />
                          Requested 2 days ago
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-18665" data-mp="13w7pc3l1n14v">
                      <button className="flex-1 sm:flex-none px-4 py-2 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 font-medium text-sm transition-colors dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700" data-id="element-18666" data-mp="ztl2kh38c6ej">
                        Decline
                      </button>
                      <button className={`flex-1 sm:flex-none px-4 py-2 rounded-md font-medium text-sm transition-colors ${rt.primaryButton}`} data-id="element-18667" data-mp="p9sg7qfr0x4p">
                        Accept Request
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Outgoing */}
            <div data-id="element-18668" data-mp="18x0023rnuuq6">
              <h2 className={`text-lg font-bold mb-4 ${dk.textPrimary}`} data-id="element-18669" data-mp="18vf54d1fp9rs">
                Sent Requests
              </h2>
              <div className={`p-5 rounded-xl border shadow-sm flex flex-col sm:flex-row gap-5 items-start sm:items-center justify-between ${dk.card}`} data-id="element-18670" data-mp="u5pza4iechl6">
                <div className="flex items-center gap-4" data-id="element-18671" data-mp="1muwsqp2zugzk">
                  <div className="w-14 h-14 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center" data-id="element-18672" data-mp="1rnpxhcygppbn">
                    <StoreIcon className={`h-6 w-6 ${dk.textMuted}`} data-id="element-18673" data-mp="3cclgjbf8944o" />
                  </div>
                  <div data-id="element-18674" data-mp="37dh7cksaz6ec">
                    <h3 className={`font-bold text-lg ${dk.textPrimary}`} data-id="element-18675" data-mp="37ea3ysxjsfir">
                      The Velvet Bookmark
                    </h3>
                    <p className={`text-sm ${dk.textSecondary} mb-1`} data-id="element-18676" data-mp="3h4lkrg2byj72">
                      Requested to supply:{' '}
                      <span className="font-medium" data-id="element-18677" data-mp="2tdzdhr05h2y4">3 titles</span>
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 ${rt.badgeRadius} text-xs font-medium bg-amber-100 text-amber-800 flex items-center`} data-id="element-18678" data-mp="2oh90jcjn6ngx">
                  <ClockIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-18679" data-mp="2xg2z38gj9nx4" />
                  Pending Review
                </span>
              </div>
            </div>
          </div>}

        {/* Tab Content: Active */}
        {activeTab === 'active' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-18680" data-mp="cshpbgpgt76a">
            {activePartnerships.map(store => <div key={store.id} className={`p-6 rounded-xl border shadow-sm ${dk.card}`} data-id="element-18681" data-mp="11r2zqrh43yl6">
                <div className="flex items-start justify-between mb-4" data-id="element-18682" data-mp="15s4ogedeks8w">
                  <div className="flex items-center gap-4" data-id="element-18683" data-mp="1ak6ags0dhxc4">
                    <img src={store.logo} alt={store.name} className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-700" data-id="element-18684" data-mp="1alr4mgirze9g" />
                    <div data-id="element-18685" data-mp="1pgdu9r7vcwqv">
                      <h3 className={`font-bold text-lg ${dk.textPrimary}`} data-id="element-18686" data-mp="1pi8f75j54ouy">
                        {store.name}
                      </h3>
                      <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-18687" data-mp="1tg638516d4wr">
                        <MapPinIcon className="h-3.5 w-3.5 mr-1" data-id="element-18688" data-mp="3v93qzmg3h7au" />
                        {store.city ? `${store.city}, ` : ''}
                        {store.country}
                      </div>
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800" data-id="element-18689" data-mp="3iybe23e9rokk">
                    Active
                  </span>
                </div>

                <div className={`py-3 border-y mb-4 flex justify-between ${dk.borderSubtle}`} data-id="element-18690" data-mp="39wv2vtfmrifa">
                  <div data-id="element-18691" data-mp="5kzmnsmd1nsv">
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-18692" data-mp="5lsvr08skpe6">Books Supplied</p>
                    <p className={`font-medium ${dk.textPrimary}`} data-id="element-18693" data-mp="1imsm1gz7ebx">3 titles</p>
                  </div>
                  <div data-id="element-18694" data-mp="3spyyhkces5cj">
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-18695" data-mp="3soe7g4paz4jf">Partner Since</p>
                    <p className={`font-medium ${dk.textPrimary}`} data-id="element-18696" data-mp="2m9wrvbaa52or">Oct 2023</p>
                  </div>
                </div>

                <div className="flex gap-3" data-id="element-18697" data-mp="2h9hdqhi6br5q">
                  <Link to={`/bookstores/${store.slug}`} className={`flex-1 text-center px-4 py-2 rounded-md font-medium text-sm transition-colors ${rt.secondaryButton}`} data-id="element-18698" data-mp="1xbe89kzrefer">
                    View Profile
                  </Link>
                  <Link to="/vendors/store-listings" className={`flex-1 text-center px-4 py-2 rounded-md font-medium text-sm transition-colors border ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`} data-id="element-18699" data-mp="265j64odb8r3s">
                    Manage Links
                  </Link>
                </div>
              </div>)}
          </div>}

        {/* Tab Content: Discover */}
        {activeTab === 'discover' && <div data-id="element-18700" data-mp="unrkkoku8qmn">
            <div className={`p-6 rounded-xl border mb-6 flex items-center justify-between ${dk.isDark ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-100'}`} data-id="element-18701" data-mp="upazaaquitmb">
              <div data-id="element-18702" data-mp="gjj547zy0dpm">
                <h3 className={`font-bold text-lg ${dk.isDark ? 'text-indigo-300' : 'text-indigo-900'}`} data-id="element-18703" data-mp="gl3xps2p5mku">
                  Suggested Partners
                </h3>
                <p className={`text-sm mt-1 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-700'}`} data-id="element-18704" data-mp="6r3kt416bd1k">
                  Based on your genres (Mystery, Thriller, Romance), these
                  stores might be a great fit.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-18705" data-mp="1rqrq1jpl9qj">
              {suggestedStores.map(store => <div key={store.id} className={`flex flex-col rounded-xl border shadow-sm overflow-hidden ${dk.card}`} data-id="element-18706" data-mp="3oyje3jnhqvnh">
                  <div className="h-24 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 relative" data-id="element-18707" data-mp="1i6ijqdcy0ezb">
                    {store.bannerImage && <img src={store.bannerImage} alt="" className="w-full h-full object-cover opacity-60" data-id="element-18708" data-mp="1ku435oz57ght" />}
                  </div>
                  <div className="px-5 pb-5 flex-1 flex flex-col" data-id="element-18709" data-mp="16tjj6phhremq">
                    <img src={store.logo} alt={store.name} className="w-16 h-16 rounded-xl object-cover border-4 shadow-sm -mt-8 mb-3 z-10 relative" style={{
                borderColor: dk.isDark ? '#1f2937' : '#ffffff'
              }} data-id="element-18710" data-mp="16v4be8gc2lhf" />
                    <h3 className={`font-bold text-lg line-clamp-1 ${dk.textPrimary}`} data-id="element-18711" data-mp="1pr2wi5azfjey">
                      {store.name}
                    </h3>
                    <div className={`flex items-center text-xs mb-3 ${dk.textSecondary}`} data-id="element-18712" data-mp="2ifi6gdf9bbjf">
                      <MapPinIcon className="h-3 w-3 mr-1" data-id="element-18713" data-mp="2ptskvecchs5t" />
                      {store.city ? `${store.city}, ` : ''}
                      {store.country}
                    </div>

                    <div className="mb-4" data-id="element-18714" data-mp="2zoasuvoso9vf">
                      <p className={`text-xs font-medium mb-1.5 ${dk.textMuted}`} data-id="element-18715" data-mp="34op0wdqebjtg">
                        Specializes in:
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-18716" data-mp="3dpdej7bvni5t">
                        {store.specializations.slice(0, 2).map((spec, i) => <span key={i} className={`text-[10px] px-2 py-0.5 rounded-md ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-18717" data-mp="3ihoc43j5arv7" data-mpi={i}>
                            {spec}
                          </span>)}
                      </div>
                    </div>

                    <div className="mt-auto pt-4" data-id="element-18718" data-mp="xylglirtv7bv">
                      <button className={`w-full py-2 rounded-md font-medium text-sm transition-colors ${rt.primaryButton}`} data-id="element-18719" data-mp="t0jvksaucxxr">
                        Propose Partnership
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
};
export default BookstorePartnerships;